/**
 * Session lifecycle and the action layer. SPEC.md §6.4, §16.3, §17.2, §35.
 *
 * Mounting this on the table screen does four things: prove who we are from the
 * stored reconnect token (or send the player back to a join flow), open and
 * authenticate the socket, hold the screen awake for the length of a hand, and
 * expose every mutation as a typed function that emits intent and turns a failed
 * ack into a toast. §15.1, §35
 *
 * The lifecycle is refcounted at module scope rather than per component, so any
 * number of components may call useSession() without opening a second socket or
 * subscribing twice to the same errors.
 */

import { useEffect, useMemo } from 'react';
import {
  CLIENT_EVENTS,
  UNDO_TOAST_DURATION_MS,
  clearHandEntryInputSchema,
  completeRoundInputSchema,
  formatSigned,
  handEntryImpact,
  messageForErrorCode,
  moveSeatInputSchema,
  recordDrinkInputSchema,
  removePlayerInputSchema,
  selectDealerInputSchema,
  setHandEntryInputSchema,
  setLockedInputSchema,
  startRoundInputSchema,
  updateProfileInputSchema,
  voidRoundInputSchema,
  type EntryShape,
  type ErrorCode,
  type UpdateProfileInput,
  type WireError,
} from '@card-counter/shared';
import { errorCodeOf, reconnectSession } from '@/lib/api';
import { haptic } from '@/lib/haptics';
import { newIdempotencyKey } from '@/lib/idempotency';
import {
  connectSession,
  disconnectSession,
  emitWithAck,
  onWireError,
  requestSnapshot,
  type ClientEventName,
  type ClientEventPayload,
} from '@/lib/socket';
import { clearPlayerToken, readPlayerToken, writePlayerToken } from '@/lib/storage';
import { acquireWakeLock, releaseWakeLock } from '@/lib/wakelock';
import { navigate, paths, useRoute } from '@/router';
import { canInteract, useConnectionStore } from '@/stores/connection-store';
import {
  canEnterAs,
  hasSnapshot,
  isRoundActive,
  playersBySeat,
  useSessionStore,
} from '@/stores/session-store';
import { HAND_ENTRY_TOAST, pushToast, useUiStore } from '@/stores/ui-store';

/**
 * Codes that mean "this device can no longer be this player". Note that
 * SESSION_ENDED is *not* one: an ended game still shows its final scores. §52
 */
const IDENTITY_LOST_CODES: readonly ErrorCode[] = [
  'INVALID_TOKEN',
  'PLAYER_REMOVED',
  'SESSION_NOT_FOUND',
];

// --- Lifecycle ---------------------------------------------------------------

let lifecycleSessionId: string | null = null;
let subscribers = 0;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribeErrors: (() => void) | null = null;
let bootstrap: AbortController | null = null;

function returnToLanding(sessionId: string | null, code: ErrorCode): void {
  if (sessionId) clearPlayerToken(sessionId);
  pushToast({ id: 'identity', message: messageForErrorCode(code) });
  navigate(paths.landing(), { replace: true });
}

function handleWireError(error: WireError): void {
  pushToast({ message: error.message });
  if (IDENTITY_LOST_CODES.includes(error.code)) {
    returnToLanding(lifecycleSessionId, error.code);
  }
}

/**
 * Proves identity over REST before opening a socket, because a bad token is
 * worth finding out about once rather than on every reconnect attempt. §6.4, §16.3
 */
async function proveIdentityAndConnect(sessionId: string, signal: AbortSignal): Promise<void> {
  const stored = readPlayerToken(sessionId);
  if (!stored) {
    // A session id on its own cannot produce a join code, so there is nowhere to
    // send them but the landing page. §34.5
    returnToLanding(sessionId, 'INVALID_TOKEN');
    return;
  }

  try {
    const credentials = await reconnectSession(sessionId, stored, signal);
    if (signal.aborted) return;
    // The server may hand back a rotated token; keep whatever it gave us. §6.4
    writePlayerToken(sessionId, credentials.playerToken);
    connectSession({ sessionId, playerToken: credentials.playerToken });
  } catch (err) {
    if (signal.aborted) return;
    if (err instanceof DOMException && err.name === 'AbortError') return;

    const code = errorCodeOf(err);
    if (IDENTITY_LOST_CODES.includes(code)) {
      returnToLanding(sessionId, code);
      return;
    }
    // A flaky network is not a rejected identity: open the socket anyway and let
    // its own retry loop win the race. §16.1, §16.3
    connectSession({ sessionId, playerToken: stored });
  }
}

function startLifecycle(sessionId: string): void {
  lifecycleSessionId = sessionId;
  unsubscribeErrors = onWireError(handleWireError);
  bootstrap = new AbortController();
  void proveIdentityAndConnect(sessionId, bootstrap.signal);
}

function stopLifecycle(): void {
  bootstrap?.abort();
  bootstrap = null;
  unsubscribeErrors?.();
  unsubscribeErrors = null;
  lifecycleSessionId = null;
  disconnectSession();
  void releaseWakeLock();
  // Sheets and the keypad draft go, toasts stay: a player redirected off the
  // table still needs to read why. §35
  useUiStore.getState().reset({ keepToasts: true });
}

function attachLifecycle(sessionId: string): () => void {
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  if (lifecycleSessionId !== sessionId) {
    stopLifecycle();
    startLifecycle(sessionId);
  }
  subscribers += 1;

  return () => {
    subscribers -= 1;
    if (subscribers > 0) return;
    // Deferred: React's development double-mount unmounts before remounting, and
    // dropping the socket in between would refetch the whole session for nothing.
    teardownTimer = setTimeout(() => {
      teardownTimer = null;
      if (subscribers === 0) stopLifecycle();
    }, 150);
  };
}

// --- Actions -----------------------------------------------------------------

/**
 * V1 has no offline write queue: queued writes create ambiguous ordering and
 * duplicate submissions. The controls are disabled while disconnected, and this
 * catches the race where a tap lands in the same frame as a drop. §16.4
 */
function writable(): boolean {
  if (canInteract(useConnectionStore.getState())) return true;
  pushToast({ id: 'offline', message: 'Reconnecting… hold that thought.' });
  return false;
}

function fail(code: ErrorCode): false {
  pushToast({ message: messageForErrorCode(code) });
  return false;
}

async function emitAction<E extends ClientEventName>(
  event: E,
  payload: ClientEventPayload<E>,
): Promise<boolean> {
  if (!writable()) return false;
  const ack = await emitWithAck(event, payload);
  if (!ack.ok) {
    // Copy comes from the wire error, which is always ERROR_MESSAGES text. §35
    pushToast({ message: ack.error.message });
    haptic('error');
    return false;
  }
  return true;
}

/**
 * Reports this player's loss for the hand. Sends the magnitude and what it does
 * to the rest of the table — never a resulting total, never the individual
 * transfers, and never who won: the server derives all of that from every report
 * standing for the hand. §15.1, §3.1
 *
 * A report is a value, not an increment, so this replaces whatever the player
 * had standing. The server does that by writing inverses of the old rows rather
 * than deleting them, which is why a correction is one call and still leaves the
 * ledger readable. §12.2
 */
export async function setHandEntry(amount: number, shape: EntryShape): Promise<boolean> {
  const state = useSessionStore.getState();
  const ui = useUiStore.getState();

  const round = state.activeRound;
  if (!round) return fail('ROUND_NOT_ACTIVE');

  // The host may be entering this for someone else. §30
  const actedFor = ui.controllingPlayerId;
  const actorPlayerId = actedFor ?? state.permissions?.playerId ?? null;
  if (!actorPlayerId) return fail('PLAYER_NOT_FOUND');
  if (!canEnterAs(state, actorPlayerId)) return fail('NOT_A_PARTICIPANT');
  if (!writable()) return false;

  // Immediate feedback. The visible total still waits for the server. §36
  haptic('tap');

  const parsed = setHandEntryInputSchema.safeParse({
    roundId: round.id,
    amount,
    shape,
    // One key per commit, so a deliberate correction is never mistaken for a
    // retry of the report it replaces. §12.4
    idempotencyKey: newIdempotencyKey(),
    actorPlayerId: actedFor ?? undefined,
  });
  if (!parsed.success) return fail('INVALID_AMOUNT');

  const ack = await emitWithAck(CLIENT_EVENTS.HAND_ENTRY_SET, parsed.data);
  if (!ack.ok) {
    pushToast({ message: ack.error.message });
    haptic('error');
    /*
      A contradiction is the one refusal the player has to *do* something about —
      the hand is telling one story and this report tells another, so somebody
      has to enter it again. The snackbar carries the reason, because colour may
      never be the only signal (§19), and the number display flashes so the
      refusal is noticed at all on the surface the thumb is already on. §3.2
    */
    if (ack.error.code === 'CONFLICTING_HAND_REPORT') useUiStore.getState().rejectEntry();
    return false;
  }

  // What the reporting player's own score moves by, from the shared rule — for a
  // loss to everyone that is the whole fan-out, not the number they typed. §2.1
  const impact = handEntryImpact({
    shape: parsed.data.shape,
    amount: parsed.data.amount,
    participantCount: round.participantIds.length,
  });

  /**
   * A report that finished the hand closed it, and a closed hand has no report
   * left to withdraw — so it gets no UNDO. Saying so is the point: the boundary
   * announces itself a moment later as a burst of cards over the roster, and the
   * way back from there is the host deleting the hand. §4.3, §4.4, §12.5
   *
   * This is the reporter's own device. Everybody else's entry notice is taken
   * down by the `round:completed` frame in `lib/socket.ts`, which is the only
   * thing the rest of the table sees. §15.2
   */
  if (!ack.data.handOpen) {
    useUiStore.getState().dismissToast(HAND_ENTRY_TOAST);
    return true;
  }

  pushToast({
    id: HAND_ENTRY_TOAST,
    message: `Reported ${formatSigned(impact)}`,
    // Withdrawing is the only undo there is: a derived row cannot be cancelled
    // on its own, because the next re-derivation would write it back. §12.2
    actionLabel: 'UNDO',
    onAction: () => {
      void clearHandEntry();
    },
    durationMs: UNDO_TOAST_DURATION_MS,
  });
  return true;
}

/**
 * Withdraws this player's report for the hand. The server cancels whatever rows
 * it had derived with inverse transfers; no transfer is ever deleted. §12.2
 */
export async function clearHandEntry(): Promise<boolean> {
  const state = useSessionStore.getState();
  const round = state.activeRound;
  if (!round) return fail('ROUND_NOT_ACTIVE');

  const actedFor = useUiStore.getState().controllingPlayerId;
  const parsed = clearHandEntryInputSchema.safeParse({
    roundId: round.id,
    idempotencyKey: newIdempotencyKey(),
    actorPlayerId: actedFor ?? undefined,
  });
  if (!parsed.success) return fail('VALIDATION_FAILED');

  const ok = await emitAction(CLIENT_EVENTS.HAND_ENTRY_CLEAR, parsed.data);
  if (ok) {
    useUiStore.getState().dismissToast(HAND_ENTRY_TOAST);
    haptic('commit');
  }
  return ok;
}

/**
 * Records a finished drink, which in drinking mode is how a player comes back
 * towards level. §4.6
 *
 * It sends no amount. What a cup is worth was fixed when the table was created
 * and lives on the session, so the server is the only thing that ever names the
 * figure — a client that could name it could award itself any number of points.
 * §15.1, §39
 *
 * The cheer is not started here. It arrives with the server's broadcast, the
 * same frame every other phone at the table gets it in, so the player who
 * confirmed sees it at the same moment everybody else does rather than a
 * round-trip earlier. §4.6, §15.3
 */
export async function recordDrink(): Promise<boolean> {
  const state = useSessionStore.getState();
  const round = state.activeRound;
  if (!round) return fail('ROUND_NOT_ACTIVE');

  // The host may be recording this for someone else, exactly as with a report. §30
  const actedFor = useUiStore.getState().controllingPlayerId;
  const parsed = recordDrinkInputSchema.safeParse({
    roundId: round.id,
    idempotencyKey: newIdempotencyKey(),
    actorPlayerId: actedFor ?? undefined,
  });
  if (!parsed.success) return fail('VALIDATION_FAILED');

  const ok = await emitAction(CLIENT_EVENTS.DRINK_RECORD, parsed.data);
  if (ok) haptic('commit');
  return ok;
}

/**
 * Opens the next hand, and it carries nothing at all: there is no mode to pick
 * and nobody to designate, because both are read back out of what the table
 * reports. The server completes a hand that is still open first, so nobody has
 * to close one by hand — and anyone may do it. §3, §4.3, §5.1
 */
export async function startHand(): Promise<boolean> {
  const parsed = startRoundInputSchema.safeParse({});
  if (!parsed.success) return fail('VALIDATION_FAILED');

  const ok = await emitAction(CLIENT_EVENTS.ROUND_START, parsed.data);
  if (ok) haptic('commit');
  return ok;
}

/**
 * Closes the current hand without starting another — the end of the game, or a
 * hand started by mistake. The ordinary path never calls this: a hand closes
 * itself once the table has told it enough. §4.4, §4.3
 */
export async function completeRound(roundId?: string): Promise<boolean> {
  const id = roundId ?? useSessionStore.getState().activeRound?.id;
  const parsed = completeRoundInputSchema.safeParse({ roundId: id });
  if (!parsed.success) return fail('ROUND_NOT_FOUND');
  return emitAction(CLIENT_EVENTS.ROUND_COMPLETE, parsed.data);
}

/**
 * Host deletes a hand so the table can play it again. §12.5
 *
 * Since hands close themselves there is no window left to withdraw a report in,
 * so this is the way back from a mistyped one. Every point the hand moved comes
 * off with an inverse; nothing is deleted from the ledger. §12.2
 */
export async function voidRound(roundId: string): Promise<boolean> {
  const parsed = voidRoundInputSchema.safeParse({ roundId });
  if (!parsed.success) return fail('ROUND_NOT_FOUND');

  const ok = await emitAction(CLIENT_EVENTS.ROUND_VOID, parsed.data);
  if (ok) {
    // Whatever the snackbar was offering belonged to the hand that just went. §12.1
    useUiStore.getState().dismissToast(HAND_ENTRY_TOAST);
    haptic('commit');
    pushToast({ id: 'round-voided', message: 'Hand deleted. Enter it again.' });
  }
  return ok;
}

/**
 * Moves a seat. Players move themselves; the host may move anyone, and the
 * server swaps with whoever holds the target seat. §9.3, §9.4
 */
export async function moveSeat(toSeatIndex: number, playerId?: string): Promise<boolean> {
  const parsed = moveSeatInputSchema.safeParse({ playerId, toSeatIndex });
  if (!parsed.success) return fail('INVALID_SEAT');
  return emitAction(CLIENT_EVENTS.PLAYER_MOVE_SEAT, parsed.data);
}

/** Name and avatar, the only profile there is. §6.3, §8.6 */
export async function updateProfile(input: UpdateProfileInput): Promise<boolean> {
  const parsed = updateProfileInputSchema.safeParse(input);
  if (!parsed.success) return fail('VALIDATION_FAILED');
  return emitAction(CLIENT_EVENTS.PLAYER_UPDATE_PROFILE, parsed.data);
}

/**
 * The host names who deals the hand in progress. §9.6, §5.1
 *
 * It sends a *player*, not an offset: what to store is the server's arithmetic,
 * derived from the seating it actually holds. A client computing the number
 * itself would be computing it against a ring that may have moved since. §60
 */
export async function selectDealer(roundId: string, playerId: string): Promise<boolean> {
  const parsed = selectDealerInputSchema.safeParse({ roundId, playerId });
  if (!parsed.success) return false;
  return emitAction(CLIENT_EVENTS.ROUND_SELECT_DEALER, parsed.data);
}

/** Host removes a player. Their history survives. §31 */
export async function removePlayer(playerId: string): Promise<boolean> {
  const parsed = removePlayerInputSchema.safeParse({ playerId });
  if (!parsed.success) return fail('PLAYER_NOT_FOUND');
  return emitAction(CLIENT_EVENTS.PLAYER_REMOVE, parsed.data);
}

/** Host locks the table against new joins; existing players still reconnect. §27.1 */
export async function setLocked(locked: boolean): Promise<boolean> {
  const parsed = setLockedInputSchema.safeParse({ locked });
  if (!parsed.success) return fail('VALIDATION_FAILED');
  return emitAction(
    locked ? CLIENT_EVENTS.SESSION_LOCK : CLIENT_EVENTS.SESSION_UNLOCK,
    parsed.data,
  );
}

/** Host ends the game. §32.1 */
export async function endGame(): Promise<boolean> {
  return emitAction(CLIENT_EVENTS.GAME_END, null);
}

/** Manual resync, for the integrity warning and the reconnect overlay. §14.3 */
export function resync(): void {
  void requestSnapshot();
}

// --- Hook --------------------------------------------------------------------

export interface SessionActions {
  setHandEntry(amount: number, shape: EntryShape): Promise<boolean>;
  clearHandEntry(): Promise<boolean>;
  /** Drinking mode only: one finished drink, priced by the session. §4.6 */
  recordDrink(): Promise<boolean>;
  startHand(): Promise<boolean>;
  completeRound(roundId?: string): Promise<boolean>;
  voidRound(roundId: string): Promise<boolean>;
  moveSeat(toSeatIndex: number, playerId?: string): Promise<boolean>;
  updateProfile(input: UpdateProfileInput): Promise<boolean>;
  removePlayer(playerId: string): Promise<boolean>;
  /** Host only. §9.6, §5.1 */
  selectDealer(roundId: string, playerId: string): Promise<boolean>;
  setLocked(locked: boolean): Promise<boolean>;
  endGame(): Promise<boolean>;
  resync(): void;
}

export interface UseSessionResult extends SessionActions {
  sessionId: string | null;
  /** Authoritative state has been applied at least once. §16.3 */
  ready: boolean;
  /** Whether mutations may be attempted at all. §16.4 */
  interactive: boolean;
}

const ACTIONS: SessionActions = {
  setHandEntry,
  clearHandEntry,
  recordDrink,
  startHand,
  completeRound,
  voidRound,
  moveSeat,
  updateProfile,
  removePlayer,
  selectDealer,
  setLocked,
  endGame,
  resync,
};

export function useSession(): UseSessionResult {
  const route = useRoute();
  const sessionId = route.name === 'game' ? route.sessionId : null;

  useEffect(() => {
    if (!sessionId) return;
    return attachLifecycle(sessionId);
  }, [sessionId]);

  const roundActive = useSessionStore(isRoundActive);
  useEffect(() => {
    // Held for the length of a hand and no longer. Failure is silent. §17.2
    if (roundActive) void acquireWakeLock();
    else void releaseWakeLock();
  }, [roundActive]);

  // A table with no mutations yet legitimately sits at sequence 0, so readiness is
  // "a snapshot has landed", never "the sequence has moved". §16.3
  const ready = useSessionStore(hasSnapshot);
  const interactive = useConnectionStore(canInteract);

  return useMemo(
    () => ({ ...ACTIONS, sessionId, ready, interactive }),
    [sessionId, ready, interactive],
  );
}
